import ApiService from './apiService';

/**
 * Service pour gérer les opérations liées aux entreprises
 */
class CompanyService {
    /**
     * Récupérer la liste des entreprises avec pagination et recherche
     * @param {Object} params Paramètres de recherche et pagination
     * @param {String} [params.search] Terme de recherche (nom, email, téléphone, adresse, ninea, rccm)
     * @param {Number} [params.per_page] Nombre d'éléments par page (défaut: 15)
     * @param {Number} [params.page] Numéro de page
     * @param {String} [params.sort_by] Champ de tri (name, email, phone, created_at)
     * @param {String} [params.sort_direction] Direction du tri (asc, desc)
     * @returns {Promise} Réponse de l'API avec la liste des entreprises
     */
    getCompanies(params = {}) {
        return ApiService.get('/companies', params);
    }

    /**
     * Récupérer une entreprise par son ID
     * @param {Number} id ID de l'entreprise
     * @returns {Promise} Réponse de l'API avec les détails de l'entreprise
     */
    getCompany(id) {
        return ApiService.get(`/companies/${id}`);
    }

    /**
     * Mettre à jour une entreprise existante
     * @param {Number} id ID de l'entreprise à mettre à jour
     * @param {Object} companyData Nouvelles données de l'entreprise
     * @param {String} [companyData.name] Nom de l'entreprise
     * @param {String} [companyData.business_type] Type d'activité
     * @param {String} [companyData.address] Adresse
     * @param {String} [companyData.phone] Téléphone
     * @param {String} [companyData.email] Email (unique, sauf pour l'entreprise actuelle)
     * @param {String} [companyData.ninea] Numéro d'identification nationale
     * @param {String} [companyData.rccm] Registre du commerce
     * @param {String} [companyData.tax_id] Numéro d'identification fiscale
     * @param {String} [companyData.website] Site web (URL valide)
     * @param {String} [companyData.description] Description de l'entreprise
     * @param {File} [companyData.logo] Logo de l'entreprise (image, max 2MB)
     * @param {String} [companyData.currency] Devise
     * @param {Number} [companyData.cash_register_start_balance] Solde de départ de la caisse
     * @param {Number} [companyData.max_cash_registers] Nombre maximum de caisses (null = infini)
     * @returns {Promise} Réponse de l'API avec l'entreprise mise à jour
     */
    updateCompany(id, companyData) {
        // Si un logo est fourni, utiliser FormData pour l'upload
        if (companyData.logo instanceof File) {
            const formData = new FormData();
            Object.keys(companyData).forEach(key => {
                if (key === 'logo') {
                    formData.append('logo', companyData.logo);
                } else if (companyData[key] !== null && companyData[key] !== undefined) {
                    formData.append(key, companyData[key]);
                }
            });
            return ApiService.put(`/companies/${id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
        }
        return ApiService.put(`/companies/${id}`, companyData);
    }

    /**
     * Supprimer une entreprise
     * @param {Number} id ID de l'entreprise à supprimer
     * @returns {Promise} Réponse de l'API
     * @throws {Error} Si l'entreprise a des utilisateurs ou des ventes
     */
    deleteCompany(id) {
        return ApiService.delete(`/companies/${id}`);
    }

    /**
     * Uploader un logo pour une entreprise
     * @param {Number} id ID de l'entreprise
     * @param {File} logoFile Fichier image du logo (max 2MB)
     * @returns {Promise} Réponse de l'API avec les informations du média uploadé
     */
    uploadLogo(id, logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);

        return ApiService.post(`/companies/${id}/logo`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    /**
     * Supprimer le logo d'une entreprise
     * @param {Number} id ID de l'entreprise
     * @returns {Promise} Réponse de l'API
     */
    deleteLogo(id) {
        return ApiService.delete(`/companies/${id}/logo`);
    }

    /**
     * Récupérer l'entreprise actuelle
     * @returns {Promise} Réponse de l'API avec les informations de l'entreprise actuelle
     */
    getCompanyCurrent() {
        return ApiService.get('/companies/current');
    }
}

export default new CompanyService();